import {
    AssessmentOutlined,
    CheckCircleOutlined,
    PaymentsOutlined,
    ReceiptLongOutlined,
    WorkOutlineOutlined,
} from '@mui/icons-material';
import { alpha, Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import useTranslate from 'hooks/useTranslate';
import type { BillingRevenueResponse } from 'types';
import { formatCurrency } from 'utils/formatters';

interface KpiCardProps {
    label: string;
    icon: ReactNode;
    value: string;
    footer: ReactNode;
    loading: boolean;
}

const KpiCard = ({ label, icon, value, footer, loading }: KpiCardProps) => (
    <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 3, borderColor: 'rgba(148, 163, 184, 0.24)' }}
    >
        <Stack spacing={1.25}>
            <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        color: 'primary.main',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    }}
                >
                    {icon}
                </Box>
            </Stack>
            {loading ? (
                <Skeleton variant="text" width="60%" height={40} />
            ) : (
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {value}
                </Typography>
            )}
            {loading ? <Skeleton variant="text" width="45%" /> : footer}
        </Stack>
    </Paper>
);

interface RevenueSummaryCardsProps {
    loading: boolean;
    revenue: BillingRevenueResponse | null;
    currency: string | null;
}

const RevenueSummaryCards = ({ loading, revenue, currency }: RevenueSummaryCardsProps) => {
    const { translate } = useTranslate();
    const caseTotals = revenue?.totals;
    const assessmentTotals = revenue?.assessmentRevenue.totals;
    const serviceAmount = currency && caseTotals ? (caseTotals.amounts[currency] ?? 0) : null;
    const assessmentAmount =
        currency && assessmentTotals ? (assessmentTotals.amounts[currency] ?? 0) : null;
    const totalAmount =
        currency && (caseTotals || assessmentTotals)
            ? (serviceAmount ?? 0) + (assessmentAmount ?? 0)
            : null;
    const empty = '—';

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(5, minmax(0, 1fr))',
                },
            }}
        >
            <KpiCard
                label={translate('serviceProviderBilling.revenue.amountDue')}
                icon={<PaymentsOutlined fontSize="small" />}
                loading={loading}
                value={
                    totalAmount != null && currency ? formatCurrency(totalAmount, currency) : empty
                }
                footer={
                    <Typography variant="caption" color="text.secondary">
                        {currency ?? translate('serviceProviderBilling.revenue.noCurrency')}
                    </Typography>
                }
            />
            <KpiCard
                label={translate('serviceProviderBilling.revenue.serviceRevenue')}
                icon={<ReceiptLongOutlined fontSize="small" />}
                loading={loading}
                value={
                    serviceAmount != null && currency
                        ? formatCurrency(serviceAmount, currency)
                        : empty
                }
                footer={
                    <Typography variant="caption" color="text.secondary">
                        {translate('serviceProviderBilling.revenue.selectedCountries')}
                    </Typography>
                }
            />
            <KpiCard
                label={translate('serviceProviderBilling.revenue.assessmentRevenue')}
                icon={<AssessmentOutlined fontSize="small" />}
                loading={loading}
                value={
                    assessmentAmount != null && currency
                        ? formatCurrency(assessmentAmount, currency)
                        : empty
                }
                footer={
                    <Typography variant="caption" color="text.secondary">
                        {translate('serviceProviderBilling.revenue.allCountries')}
                    </Typography>
                }
            />
            <KpiCard
                label={translate('serviceProviderBilling.revenue.billableCases')}
                icon={<WorkOutlineOutlined fontSize="small" />}
                loading={loading}
                value={caseTotals ? caseTotals.cases.toLocaleString('en-US') : empty}
                footer={
                    <Typography variant="caption" color="text.secondary">
                        {translate('serviceProviderBilling.revenue.billableCasesHint')}
                    </Typography>
                }
            />
            <KpiCard
                label={translate('serviceProviderBilling.revenue.assessmentsCompleted')}
                icon={<CheckCircleOutlined fontSize="small" />}
                loading={loading}
                value={
                    assessmentTotals ? assessmentTotals.assessments.toLocaleString('en-US') : empty
                }
                footer={
                    <Typography variant="caption" color="text.secondary">
                        {translate('serviceProviderBilling.revenue.allCountries')}
                    </Typography>
                }
            />
        </Box>
    );
};

export default RevenueSummaryCards;
